import {
  BoxGeometry,
  CylinderGeometry,
  Group,
  IcosahedronGeometry,
  Mesh,
  MeshLambertMaterial,
  SphereGeometry,
  type BufferGeometry,
  type Material,
} from 'three';
import { buildHippo, PLUSH_COLORS } from '../models/hippo';

/**
 * JEFE-1 El Patrón (GDD §9.2, §4.3, §3.4, issue #96): a parody caricature, never a real name or insignia. A flashy
 * private helicopter (teal and orange) with a plush toy hippo hanging from a rope, and the boss himself in the
 * cockpit: a round head with an enormous moustache and curls, a belly and a plain yellow football shirt (no crest,
 * no number, no team). Low-poly with flat shading, made of primitives. About 3.2 x 2 u. The retreat set is a generic
 * cathedral with a toy football pitch, where the helicopter lands out of fuel: nobody crashes (GDD §3.4).
 */
export interface Jefe1Model {
  /** The helicopter with the boss and the plush hippo; its origin is the centre of the fuselage. */
  helicopter: Group;
  rotor: Group;
  /** Arm that goes to the moustache in the transition pose. */
  arm: Group;
  /** The hanging plush hippo, which swings. */
  plush: Group;
  /** Light puffs of an empty tank during the retreat: not the smoke of a crash. */
  fuelSmoke: Group;
  /** The La Catedral set of the retreat and its landing pad. */
  retreatSet: Group;
  pad: Mesh;
  dispose(): void;
}

export const JEFE_1_COLORS = {
  fuselage: 0x2fa7a0,
  stripe: 0xf08a24,
  glass: 0x9fd4e8,
  skin: 0xd9a37a,
  moustache: 0x2a2320,
  curls: 0x2a2320,
  shirt: 0xf2d24b,
  skid: 0x9aa0a6,
  rotor: 0x4a4f55,
  rope: 0xc9b896,
  cathedral: 0xd9cdb3,
  roof: 0x8d8070,
  pitch: 0x4f9d5d,
  line: 0xe9efe9,
  pad: 0xe9e2d0,
  puff: 0xc4c8cc,
} as const;

/** Distance from the origin of the helicopter to the bottom of its skids: it rests on the pad at this height. */
export const SKID_OFFSET = 0.95;

export function buildJefe1(): Jefe1Model {
  const geometries: BufferGeometry[] = [];
  const materials: Material[] = [];
  const paint = (color: number) => {
    const material = new MeshLambertMaterial({ color, flatShading: true });
    materials.push(material);
    return material;
  };
  const own = <T extends BufferGeometry>(geometry: T): T => {
    geometries.push(geometry);
    return geometry;
  };
  const part = (geometry: BufferGeometry, color: number, at: [number, number, number], scale?: [number, number, number]) => {
    const mesh = new Mesh(own(geometry), paint(color));
    mesh.position.set(...at);
    if (scale) mesh.scale.set(...scale);
    return mesh;
  };

  const helicopter = new Group();
  helicopter.name = 'boss-jefe-1-helicopter';
  helicopter.userData.pose = 'idle';

  // Fuselage, glass bubble, tail and skids.
  const fuselage = part(new IcosahedronGeometry(0.9, 1), JEFE_1_COLORS.fuselage, [0, 0, 0], [1.5, 0.95, 0.9]);
  const stripe = part(new BoxGeometry(2.4, 0.12, 0.95), JEFE_1_COLORS.stripe, [0, -0.2, 0]);
  const cockpit = part(new SphereGeometry(0.62, 7, 5), JEFE_1_COLORS.glass, [-0.7, 0.15, 0], [1, 0.9, 0.95]);
  const tail = part(new BoxGeometry(1.8, 0.2, 0.2), JEFE_1_COLORS.fuselage, [1.9, 0.15, 0]);
  const fin = part(new BoxGeometry(0.1, 0.55, 0.2), JEFE_1_COLORS.stripe, [2.7, 0.4, 0]);
  const skidFront = part(new BoxGeometry(2, 0.07, 0.07), JEFE_1_COLORS.skid, [-0.1, -SKID_OFFSET, 0.45]);
  const skidBack = part(new BoxGeometry(2, 0.07, 0.07), JEFE_1_COLORS.skid, [-0.1, -SKID_OFFSET, -0.45]);
  const strutA = part(new BoxGeometry(0.07, 0.5, 0.07), JEFE_1_COLORS.skid, [-0.7, -0.7, 0.45]);
  const strutB = part(new BoxGeometry(0.07, 0.5, 0.07), JEFE_1_COLORS.skid, [0.5, -0.7, 0.45]);
  helicopter.add(fuselage, stripe, cockpit, tail, fin, skidFront, skidBack, strutA, strutB);

  // The main rotor spins with render time (information: it also turns with reduced motion).
  const rotor = new Group();
  rotor.name = 'boss-jefe-1-rotor';
  rotor.position.set(0, 0.95, 0);
  rotor.add(
    part(new CylinderGeometry(0.06, 0.06, 0.2, 6), JEFE_1_COLORS.rotor, [0, 0, 0]),
    part(new BoxGeometry(3.4, 0.04, 0.2), JEFE_1_COLORS.rotor, [0, 0.1, 0]),
    part(new BoxGeometry(0.2, 0.04, 3.4), JEFE_1_COLORS.rotor, [0, 0.1, 0]),
  );
  helicopter.add(rotor);

  // The boss: head, enormous moustache, curls, belly and a plain football shirt.
  const body = new Group();
  body.name = 'boss-jefe-1-body';
  body.position.set(-0.7, 0.1, 0.1);
  const belly = part(new SphereGeometry(0.42, 7, 5), JEFE_1_COLORS.shirt, [0.05, -0.2, 0.05], [1.1, 0.9, 1]);
  const head = part(new SphereGeometry(0.28, 7, 5), JEFE_1_COLORS.skin, [0, 0.28, 0.1]);
  const moustache = part(new BoxGeometry(0.72, 0.1, 0.14), JEFE_1_COLORS.moustache, [0, 0.2, 0.34]);
  const curlsLeft = part(new IcosahedronGeometry(0.12, 0), JEFE_1_COLORS.curls, [-0.2, 0.5, 0.1]);
  const curlsMid = part(new IcosahedronGeometry(0.14, 0), JEFE_1_COLORS.curls, [0, 0.58, 0.1]);
  const curlsRight = part(new IcosahedronGeometry(0.12, 0), JEFE_1_COLORS.curls, [0.2, 0.5, 0.1]);
  const arm = new Group();
  arm.name = 'boss-jefe-1-arm';
  arm.position.set(0.25, 0, 0.3);
  arm.add(part(new BoxGeometry(0.12, 0.5, 0.12), JEFE_1_COLORS.skin, [0, -0.05, 0]));
  body.add(belly, head, moustache, curlsLeft, curlsMid, curlsRight, arm);
  helicopter.add(body);

  // The plush hippo hangs from the skid on a rope and swings (the hanging toy of the data sheet).
  const toy = buildHippo(PLUSH_COLORS, 'boss-jefe-1-plush-hippo');
  const plush = toy.body;
  plush.visible = true;
  plush.scale.setScalar(0.45);
  const rope = part(new BoxGeometry(0.03, 0.5, 0.03), JEFE_1_COLORS.rope, [0, -0.25, 0]);
  const hang = new Group();
  hang.name = 'boss-jefe-1-plush-hang';
  hang.position.set(0.6, -SKID_OFFSET, 0.2);
  plush.position.set(0, -0.8, 0);
  hang.add(rope, plush);
  helicopter.add(hang);

  const fuelSmoke = new Group();
  fuelSmoke.name = 'boss-jefe-1-fuel-smoke';
  fuelSmoke.add(
    part(new IcosahedronGeometry(0.22, 0), JEFE_1_COLORS.puff, [2.3, 0.3, 0]),
    part(new IcosahedronGeometry(0.3, 0), JEFE_1_COLORS.puff, [3.0, 0.55, 0]),
  );
  fuelSmoke.visible = false;
  helicopter.add(fuelSmoke);

  // La Catedral: a generic cathedral with two towers and a toy football pitch with the landing pad.
  const retreatSet = new Group();
  retreatSet.name = 'boss-jefe-1-retreat-la-catedral';
  const nave = part(new BoxGeometry(5, 2.6, 1.6), JEFE_1_COLORS.cathedral, [-1, 1.3, -1.2]);
  const towerA = part(new BoxGeometry(1.1, 4, 1.2), JEFE_1_COLORS.cathedral, [-3.2, 2, -1.2]);
  const towerB = part(new BoxGeometry(1.1, 4, 1.2), JEFE_1_COLORS.cathedral, [1.2, 2, -1.2]);
  const roofA = part(new CylinderGeometry(0, 0.8, 1, 4), JEFE_1_COLORS.roof, [-3.2, 4.5, -1.2]);
  const roofB = part(new CylinderGeometry(0, 0.8, 1, 4), JEFE_1_COLORS.roof, [1.2, 4.5, -1.2]);
  const pitch = part(new BoxGeometry(6.5, 0.1, 2.4), JEFE_1_COLORS.pitch, [2.2, 0.05, 0]);
  const halfway = part(new BoxGeometry(0.06, 0.02, 2.4), JEFE_1_COLORS.line, [2.2, 0.11, 0]);
  const pad = part(new CylinderGeometry(1.1, 1.1, 0.06, 10), JEFE_1_COLORS.pad, [2.2, 0.13, 0]);
  pad.name = 'boss-jefe-1-landing-pad';
  retreatSet.add(nave, towerA, towerB, roofA, roofB, pitch, halfway, pad);
  retreatSet.visible = false;

  return {
    helicopter,
    rotor,
    arm,
    fuelSmoke,
    plush: hang,
    retreatSet,
    pad,
    dispose() {
      toy.dispose();
      for (const geometry of geometries) geometry.dispose();
      for (const material of materials) material.dispose();
    },
  };
}
